import yaml from 'yaml'

import * as initialTriageData from '/assets/initial_triage.js'
import * as interventionData from '/assets/interventions.js'
import * as resourceData from '/assets/resources.js'
import * as riskFactorData from '/assets/risk_factors.js'


const isRange = (str) => !!str.trim().match(/^\d+-\d+$/)
const isNumber = (str) => !!str.trim().match(/^\d+$/)

const assessmentFormMap = {
  'initial_triage': initialTriageData.default,
  'risk_factors': riskFactorData.default,
}


export const setupLogger = (moduleName, color, ...msg) => {
  if (!color) color = 'white'
  return {
    logger: (...msg) => IS_DEV && console.log(`%c${moduleName}:`, `color:${color};`, ...msg),
    loggerError: (...msg) => console.error(`${moduleName}:`, ...msg)
  }
}


// https://davidwalsh.name/javascript-debounce-function
export function debounce(func, wait, immediate) {
  let timeout
  return function() {
    let context = this
    let args = arguments
    let later = function() {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    let callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}


export const getAssessmentData = (assessmentId = undefined) => {
  if (assessmentId) {
    // Return data for a specific assessment form
    return assessmentFormMap[assessmentId]
  } else {
    // Return all of the data
    return Object.values(assessmentFormMap).flatMap((id) => id)
  }
}


export const getAssessmentItemIds = (assessmentId = undefined) => {
  return getAssessmentData(assessmentId)
    .flatMap((group) => group.items)
    .flatMap((item) => {
      if (item.options) {
        return item.options.map((opt) => `${item.id}_${opt.value}`)
      } else {
        return item.id
      }
    })
}


export function getFocusableEls(parentElement) {
  // From Kitty Giraudel's a11y-dialog
  // https://github.com/KittyGiraudel/a11y-dialog/tree/8.0.0

  const notInert = ':not([inert]):not([inert] *)'
  const notNegTabIndex = ':not([tabindex^="-"])'
  const notDisabled = ':not(:disabled)'

  const focusableSelectors = [
    `a[href]${notInert}${notNegTabIndex}`,
    `area[href]${notInert}${notNegTabIndex}`,
    `input:not([type="hidden"]):not([type="radio"])${notInert}${notNegTabIndex}${notDisabled}`,
    `input[type="radio"]${notInert}${notNegTabIndex}${notDisabled}`,
    `select${notInert}${notNegTabIndex}${notDisabled}`,
    `textarea${notInert}${notNegTabIndex}${notDisabled}`,
    `button${notInert}${notNegTabIndex}${notDisabled}`,
    `details${notInert} > summary:first-of-type${notNegTabIndex}`,
    `iframe${notInert}${notNegTabIndex}`,
    `audio[controls]${notInert}${notNegTabIndex}`,
    `video[controls]${notInert}${notNegTabIndex}`,
    `[contenteditable]${notInert}${notNegTabIndex}`,
    `[tabindex]${notInert}${notNegTabIndex}`,
  ]

  const focusableChildren = parentElement.querySelectorAll(focusableSelectors.join(','))
  return Array.prototype.slice.call(focusableChildren)
}


export const getInterventionCategoryData = (category) => {
  return interventionData[category]
}


export const getInterventionItemData = (category, id) => {
  const groupItems = (interventionData[category].groups).flatMap((group) => group.items)
  const mergedItems = Object.assign({}, ...groupItems)
  return mergedItems[id]
}


export const getResourceItemData = (resourceId) => {
  return resourceData[resourceId]
}


export const getResourceIdsByInterventionId = (category, interventionId, interventionItemData) => {
  // This returns an array of any resource IDs in the intervention item's category
  // and group data, as well as in the item's own data.

  const categoryData = getInterventionCategoryData(category)
  const groupData = categoryData.groups.filter((group) => {
    return Object.keys(group.items).map((i) => parseInt(i)).includes(parseInt(interventionId))
  })[0]

  const itemResources = [interventionItemData, categoryData, groupData].flatMap((itemLevel) => {
    if (!itemLevel) return []
    return Object.hasOwn(itemLevel, 'resources') ? itemLevel.resources : []
  })
  // Deduplicate and sort resource IDs:
  return Array.from(new Set(itemResources)).sort((a, b) => a - b)
}


export const getRiskFactorData = (selectionIds) => {
  const assessmentData = getAssessmentData()
  const riskFactorItems = assessmentData.flatMap((category) => {
    return category.items.flatMap((item) => Object.hasOwn(item, 'options') ? item.options : item)
  })
  const selectedRiskFactors = riskFactorItems.filter((i) => selectionIds.includes(i.id))
  return selectedRiskFactors
}


export const getRiskFactorsByInterventionId = (interventionCategory, interventionId, assessmentId) => {
  const assessmentData = getAssessmentData(assessmentId)

  const factorFilter = (factor) => {
    // No interventions for this risk factor? bail
    if (!factor.interventions) return false
    // This risk factor isn't linked to the intervention category we want? bail
    if (!Object.hasOwn(factor.interventions, interventionCategory)) return false

    const parsedInterventions = parseInterventions([
      interventionCategory,
      factor.interventions[interventionCategory]
    ])
    return parsedInterventions[interventionCategory].includes(parseInt(interventionId))
  }

  return assessmentData.reduce((relevantFactors, assessmentCategory) => {
    const flattenedCategoryItems = assessmentCategory.items.flatMap((item) => item.options ? item.options : item)
    const filteredFactors = flattenedCategoryItems.filter(factorFilter)

    // If no risk factors in this category are linked to the specified intervention,
    // add nothing to relevantFactors.
    console.log('filtered factors for category', assessmentCategory.category_name, filteredFactors)
    if (!filteredFactors.length) return relevantFactors

    // Otherwise, add this category and its risk factors that are linked to the specified intervention:
    return [
      ...relevantFactors,
      {
        category_name: assessmentCategory.category_name,
        id: assessmentCategory.id,
        items: filteredFactors
      }
    ]
  }, [])
}


export const getRiskFactorMaximumScore = () => {
  const assessmentData = getAssessmentData()
  const riskFactorItems = assessmentData.flatMap((category) => category.items)
  return riskFactorItems.reduce((sum, currentItem) => {
    let currentItemScore

    if (Object.hasOwn(currentItem, 'options') && currentItem.options.length > 0) {
      // This is a radio group. Because only one selection within it is possible,
      // include only the highest available score.
      const radioScores = currentItem.options.map((i) => i.score).sort((a, b) => b - a)
      currentItemScore = radioScores[0]
    } else {
      currentItemScore = currentItem.score
    }

    // Don't include negative scores in the total:
    return currentItemScore && Math.sign(currentItemScore) === 1 ? sum + currentItemScore : sum
  }, 0)
}


export function getTransitionDuration(element, pseudoElement = '') {
  const inSeconds = /\ds$/
  const inMilliseconds = /\dms$/
  const elementStyle = window.getComputedStyle(element, pseudoElement)

  const isValidDuration = (value) => {
    return (value.match(inMilliseconds) || value.match(inSeconds))
      && (value.replace(/[^0-9]$/, '') !== '0')
  }

  const transitionDuration = elementStyle.getPropertyValue('transition-duration')
  const animationDuration = elementStyle.getPropertyValue('animation-duration')

  let durationValue

  if (isValidDuration(transitionDuration)) {
    durationValue = transitionDuration
  } else if (isValidDuration(animationDuration)) {
    durationValue = animationDuration
  } else {
    console.warn('getTransitionDuration failed: make sure that CSS transition-duration or animation-duration property is set correctly, and is declared in seconds (s) or milliseconds (ms)')
    return false
  }

  const ds = durationValue.replace(/[^0-9]$/, '')
  return durationValue.match(inMilliseconds) ? parseFloat(ds) : parseFloat(ds) * 1000
}


export const isValidAssessmentItemId = (itemId, assessmentId = undefined) => {
  return getAssessmentItemIds(assessmentId).includes(itemId)
}


export const isValidInterventionId = (category, interventionId) => {
  const groupItems = (interventionData.default[category].groups).flatMap((group) => group.items)
  const interventionItems = Object.keys(Object.assign({}, ...groupItems)).map((i) => parseInt(i))
  return interventionItems.includes(parseInt(interventionId))
}


export const parseInterventions = (selectionData) => {
  // selectionData must be an array made from one of the key-value pairs
  // within a risk factor's `interventions` object.
  //
  // Example:
  //   ["dscm", "4-6, 8-11"]
  //
  // parseInterventions returns an object containing the intervention
  // category as a key, and an array of all the indicated intervention IDs
  // as integers.
  //
  // Examples:
  //   { dscm: [4, 5, 6, 8, 9, 10, 11] }

  const intCollection = {}
  const category = selectionData[0]
  const selectedInterventions = selectionData[1].toString()

  // Get an array of every group in this intervention category:
  const groupItems = (interventionData.default[category].groups).flatMap((group) => group.items)
  // And merge the groups' values into a single object:
  const interventionItems = Object.assign({}, ...groupItems)

  if (selectedInterventions === 'all') {
    // First: check if the `selectedInterventions` value refers to all in a given group.
    intCollection[category] = (Object.keys(interventionItems)).map(i => parseInt(i))
  } else {
    // Otherwise, split the string by commas with possible whitespace on either side.
    // If any resulting array item has a hyphen, treat that item as a range.
    // If any item only contains digits, treat it as an integer.
    // If there's some other nonsense going on, handle it gracefully.
    const intSplit1 = selectedInterventions.split(/\s*,\s*/)
    const intFiltered = intSplit1.filter((item) => isRange(item) || isNumber(item))
    const intFormatted = intFiltered.flatMap((item) => {
      if (isNumber(item)) {
        return parseInt(item)
      } else if (isRange(item)) {
        const itemRange = item.split('-')
        const start = parseInt(itemRange[0])
        const stop = parseInt(itemRange[1])
        return range(start, stop)
      }
    })
    // Finally, check every formatted intervention ID to ensure it exists.
    intCollection[category] = intFormatted.filter((id) => isValidInterventionId(category, id))
  }

  return intCollection
}


export const range = (start, stop, step = 1) => {
  return Array.from(
    { length: (stop - start) / step + 1 },
    (value, index) => start + index * step
  )
}


export function viewportSize(sm, lg) {
  const VIEWPORT_IS_SM = window.matchMedia(`(max-width: ${sm})`)
  const VIEWPORT_IS_LG = window.matchMedia(`(min-width: ${lg})`)

  return {
    isSm: VIEWPORT_IS_SM,
    isLg: VIEWPORT_IS_LG
  }
}
